import Cookies from 'js-cookie';
import { defineStore } from 'pinia';
import { Draft, DraftType } from '@/dashboard/types/draft';
import { DraftGetResponse, DraftsApi } from '@/dashboard/api/drafts';
import { addImageProcess, sendGoogleEvent } from '@/dashboard/helper';
import { instance } from '@/dashboard/api/instance';
import { themeDetect } from '@/dashboard/utils/themeDetect';

const IMAGE_SCALE_DOWN_MODE = 'scale_down_mode';
const IMAGE_AUTOSCALING = 'autoscaling';
const VIDEO_SCALE_DOWN_MODE = 'video_scale_down_mode';
const SHOW_DOWNLOAD_BANNER = 'showDownloadBanner';
const SHOW_DRAWING_HELP_INFO = 'showDrawingMessage';

export type DraftStoreState = {
  draft: Draft | null;
  followed: boolean;
  downloadUrl: string | null;
  imageScaleDownMode: boolean;
  videoScaleDownMode: boolean;
  imageAutoscaling: boolean;
  showScaleDown: boolean;
  allowCommenting: boolean;
  showDownloadBanner: boolean;
  showDrawingMessage: boolean;
};

export const useDraftStore = defineStore('draftStore', {
  state: (): DraftStoreState => {
    themeDetect();

    const imageScaleDownMode = Cookies.get(IMAGE_SCALE_DOWN_MODE);
    const videoScaleDownMode = Cookies.get(VIDEO_SCALE_DOWN_MODE);

    const autoscaling = Cookies.get(IMAGE_AUTOSCALING);
    const showDownloadBanner = localStorage.getItem(SHOW_DOWNLOAD_BANNER);
    const showDrawingMessage = localStorage.getItem(SHOW_DRAWING_HELP_INFO);

    return {
      draft: null,
      followed: false,
      downloadUrl: null,
      imageScaleDownMode: imageScaleDownMode !== 'false',
      videoScaleDownMode: videoScaleDownMode !== 'false',
      imageAutoscaling: autoscaling !== 'false',
      showScaleDown: true,
      allowCommenting: false,
      showDownloadBanner: (window as any).g_opt.user.email ? (showDownloadBanner === null ? true : showDownloadBanner === 'true') : true,
      showDrawingMessage: showDrawingMessage === null ? true : showDrawingMessage === 'true',
    };
  },
  getters: {
    imageUrl(): string | null {
      return this.draft?.image?.original_url ?? null;
    },
  },
  actions: {
    async loadDraft(id: any) {
      const result = await DraftsApi.get(id);
      if (result.success && result.draft) {
        const draft = result.draft;
        const followed = result.followed;

        await addImageProcess(draft?.image?.original_url ?? '');

        this.draft = draft;
        this.followed = followed;

        const user = (window as any).g_opt.user;
        this.allowCommenting = draft.allow_comment || draft.owner_id === user.id;

        const urlResponse = await DraftsApi.getDownloadUrl({
          short_link: draft.short_link,
          filename: encodeURIComponent(draft.name),
        });
        this.downloadUrl = urlResponse.url;
      }
    },
    async loadDraftByShortLink(shortLink: string): Promise<DraftGetResponse> {
      const response = await DraftsApi.getByShortLink(shortLink);
      this.draft = response.draft;
      this.followed = response.followed;
      this.showScaleDown = this.draft?.draft_type === 'image';

      if (this.draft !== null) {
        const user = (window as any).g_opt.user;
        this.allowCommenting = this.draft.allow_comment || this.draft.owner_id === user.id;

        await addImageProcess(this.draft.image?.original_url ?? '');

        const urlResponse = await DraftsApi.getDownloadUrl({
          short_link: this.draft.short_link,
          filename: encodeURIComponent(this.draft.name),
        });
        this.downloadUrl = urlResponse.url;
      }

      // Очищаем удаленные элементы при загрузке нового черновика
      const { useDraftImageStore } = await import('./draftImageStore');
      const draftImageStore = useDraftImageStore();
      draftImageStore.setDeletedPoints([]);
      draftImageStore.setDeletedComments([]);

      return response;
    },
    setDraft(draft: Draft) {
      const user = (window as any).g_opt.user;
      this.draft = draft;
      this.allowCommenting = draft.allow_comment || draft.owner_id === user.id;
    },
    setScaleDownMode(scaleDownMode: boolean, type: DraftType = DraftType.image) {
      if (type === DraftType.image) {
        this.imageScaleDownMode = scaleDownMode;
        Cookies.set(IMAGE_SCALE_DOWN_MODE, String(scaleDownMode), { expires: 365 });
      }
      if (type === DraftType.video) {
        this.videoScaleDownMode = scaleDownMode;
        Cookies.set(VIDEO_SCALE_DOWN_MODE, String(scaleDownMode), { expires: 365 });
      }
    },
    setAutoscaling(autoscaling: boolean, type: DraftType = DraftType.image) {
      if (type === DraftType.image) {
        this.imageAutoscaling = autoscaling;
        Cookies.set(IMAGE_AUTOSCALING, String(autoscaling), { expires: 365 });
      }
    },
    changeShowDrawingMessage(needShow: boolean) {
      this.showDrawingMessage = needShow;
      localStorage.setItem(SHOW_DRAWING_HELP_INFO, String(needShow));
    },
    changeScaleDownMode(type: DraftType = DraftType.image) {
      if (type === DraftType.image) {
        const scaleDownMode = this.imageScaleDownMode;
        this.setScaleDownMode(!scaleDownMode, type);
        this.setAutoscaling(false, type);

        sendGoogleEvent('scale_down_changed', { scaleDownMode: !scaleDownMode ? 'off' : 'on' });
      }

      if (type === DraftType.video) {
        const scaleDownMode = this.videoScaleDownMode;
        this.setScaleDownMode(!scaleDownMode, type);
        sendGoogleEvent('scale_down_changed', { scaleDownMode: !scaleDownMode ? 'off' : 'on' });
      }
    },
    changeAutoscaling(originalSizeWidth: number, width: number, type: DraftType = DraftType.image) {
      if (type !== DraftType.image) {
        return;
      }

      const zoom = originalSizeWidth / width;
      const autoscaling = this.imageAutoscaling;

      this.setScaleDownMode(zoom > 1, type);
      this.setAutoscaling(!autoscaling, type);

      sendGoogleEvent('autoscaling_changed', { theme: !autoscaling ? 'off' : 'on' });
    },
    async allowDraftCommenting() {
      if (!this.draft) return;
      const id = this.draft.id;
      const response = await instance.post(`/drafts/${id}/allow_comment`, null);
      const result = response.data.result;
      const draft = { ...this.draft, allow_comment: result };
      this.setDraft(draft);
      sendGoogleEvent('allow_commenting_changed', { theme: result ? 'on' : 'off' });
    },
    async followDraft() {
      if (!this.draft) return;
      const id = this.draft.id;
      const response = await instance.post(`/drafts/${id}/follow`, null);

      this.followed = response.data.followed;
    },
    closeDownloadBanner() {
      this.showDownloadBanner = false;
      localStorage.setItem(SHOW_DOWNLOAD_BANNER, 'false');
    },
  },
});
